import { useState } from 'react';
import type { ManagerSuggestion } from '@evi/shared';
import { salesPost } from '@/lib/salesTeam';
import { approvalStaffChoice, staffPickList, type ApprovalAssign } from '@/lib/partnerApproval';
import { RegionChips } from '@/components/regions/RegionPicker';
import styles from './SalesTeam.module.css';

export interface ReferralApplicationRow {
  _id: string; name: string; email: string;
  /** Absent on a lander application, which comes with no referral (walkthrough gap 1, 21 October 2026). */
  sales_referral?: { label: string; submitted_at: string; staff_id?: string | null };
  sales_referral_decision?: unknown;
  /** The credit the application carries now: a partner approver's correction, else the join link's (21 October 2026).
   *  null: the EVI house book. Absent from an older Api, where sales_referral decides. */
  credit?: { staff_id: string | null; label: string; via: 'link' | 'named' | 'corrected' | null } | null;
  /** 25 October 2026; absent from an older Api. */
  served_regions?: string[];
  manager_suggestion?: ManagerSuggestion;
}
export interface ReviewStaff { _id: string; name?: string; email?: string; role?: 'rep' | 'manager'; end_date?: string | null }
type ApproveResult = { review_required?: boolean; existing_staff_name?: string; referring_staff_name?: string; invite_email?: { ok: boolean; error?: string; to?: string } };
/** Decline, or ask for what EVI needs (walkthrough A 036). Reads this form's decision reason and message. */
export type ApplicationAnswer = (outcome: 'declined' | 'needs_info', form: HTMLFormElement) => void | Promise<void>;

/**
 * One open application on the approval screen (Warren, 25 October 2026):
 * the applicant's regions, and who the new partner goes to. The referring
 * rep when the application came through one, else the EVI house book, unless
 * the Super Admin picks an SP Manager. The SP Managers whose territory covers
 * the most of the applicant's regions are suggested (ties all shown); a
 * suggestion is applied only by its button and assigned only by Approve.
 *
 * The partner approver's decision form too (21 October 2026): the business the
 * partner is named after (walkthrough A 041), and Ask for more or Decline,
 * which tell the applicant. The referring rep is the application's credit now,
 * a partner approver's correction included; the screen remounts this form when
 * the credit changes, so its choice starts from the corrected credit.
 */
export function ReferralApplicationForm({ row, staff, onDone, onAnswer, businessDefault }: {
  row: ReferralApplicationRow; staff: ReviewStaff[]; onDone: (message: string) => Promise<void> | void;
  onAnswer?: ApplicationAnswer; businessDefault?: string;
}) {
  const credited = row.credit !== undefined ? row.credit : (row.sales_referral ?? null);
  const referringStaff = credited?.staff_id ?? null;
  const [assign, setAssign] = useState<ApprovalAssign>(referringStaff ? 'referral' : 'house');
  const [staffId, setStaffId] = useState('');
  const [busy, setBusy] = useState(false);
  const suggestion = row.manager_suggestion;
  const candidates = suggestion?.candidates ?? [];
  const regions = row.served_regions ?? [];
  const pickList = staffPickList(staff, candidates.map((c) => c.staff_id));
  const choice = approvalStaffChoice(assign, staffId);
  const ready = choice.ok;

  return (
    <form className={styles.form} onSubmit={async (e) => {
      e.preventDefault();
      if (!choice.ok) return;
      const f = new FormData(e.currentTarget);
      const businessName = String(f.get('business_name') ?? '').trim();
      setBusy(true);
      try {
        const result = await salesPost<ApproveResult>(`admin/sales-flow/applications/${row._id}/approve`, {
          reason: f.get('reason'), resolve_existing: f.get('resolve') === 'on', ...choice.body,
          ...(candidates.length ? { suggested_staff_ids: candidates.map((c) => c.staff_id) } : {}),
          ...(businessName ? { business_name: businessName } : {}),
        });
        await onDone(result.review_required ? `Existing partner requires a decision. Current staff: ${result.existing_staff_name ?? 'House'}. Referring staff: ${result.referring_staff_name ?? 'House'}.`
          : result.invite_email?.ok ? `Approved. The invite is emailed to ${result.invite_email.to ?? row.email}.`
          : result.invite_email ? 'Approved, but the invite email did not go. Use Re-send invite or Copy invite link on Sales Partners.'
          : 'Approved. This partner already had an account, so no invite was needed.');
      } catch (error) { await onDone(error instanceof Error ? error.message : 'Approval failed.'); }
      finally { setBusy(false); }
    }}>
      <div className={styles.full}>
        <span className={styles.muted}>Regions served: </span>
        <RegionChips codes={regions} empty="Not given on the application" />
      </div>
      <fieldset className={styles.full}>
        <legend>New partner managed by</legend>
        {referringStaff && (
          <label className={styles.choice}><input type="radio" name={`assign-${row._id}`} checked={assign === 'referral'} onChange={() => setAssign('referral')} /> The referring SP Manager ({credited?.label})</label>
        )}
        <label className={styles.choice}><input type="radio" name={`assign-${row._id}`} checked={assign === 'house'} onChange={() => setAssign('house')} /> The EVI house book</label>
        <label className={styles.choice}><input type="radio" name={`assign-${row._id}`} checked={assign === 'manager'} onChange={() => setAssign('manager')} /> An SP Manager I choose</label>
        {assign === 'manager' && (
          <label>SP Manager
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} required>
              <option value="">Choose a person</option>
              {pickList.suggested.length > 0 && (
                <optgroup label="Suggested by territory">
                  {pickList.suggested.map((s) => <option key={s._id} value={s._id}>{s.name ?? s.email}</option>)}
                </optgroup>
              )}
              <optgroup label={pickList.suggested.length ? 'Everyone else' : 'Sales staff'}>
                {pickList.others.map((s) => <option key={s._id} value={s._id}>{s.name ?? s.email}{s.role === 'manager' ? ' (Sales Manager)' : ''}</option>)}
              </optgroup>
            </select>
          </label>
        )}
      </fieldset>
      {candidates.length > 0 ? (
        <div className={`${styles.full} ${styles.suggestion}`} role="note">
          <p>
            Suggested by territory: {candidates.length > 1 ? `${candidates.length} SP Managers tie, each covering` : 'covers'} {suggestion!.covered} of {suggestion!.of} region{suggestion!.of === 1 ? '' : 's'}.
            Choosing one sets the field above; nothing is assigned until you approve.
          </p>
          <div className={styles.suggestionButtons}>
            {candidates.map((c) => (
              <button key={c.staff_id} type="button" className={styles.secondary} aria-pressed={assign === 'manager' && staffId === c.staff_id}
                onClick={() => { setAssign('manager'); setStaffId(c.staff_id); }}>
                Use {c.name} ({c.covered.join(', ')})
              </button>
            ))}
          </div>
        </div>
      ) : regions.length > 0 ? (
        <p className={`${styles.full} ${styles.muted}`}>No SP Manager&rsquo;s territory covers these regions. Set territories on this page under each person.</p>
      ) : null}
      {/* The partner is named after the business (walkthrough A 041); blank keeps the applicant's name. */}
      <label>Business name<input name="business_name" maxLength={160} defaultValue={businessDefault ?? ''} placeholder={row.name} /></label>
      <label>Decision reason<input name="reason" required maxLength={2000} /></label>
      <label className={styles.choice}><input type="checkbox" name="resolve" />I explicitly approve moving an existing partner</label>
      {onAnswer && <label>Message to the applicant (needed to ask for more; optional when declining)<textarea name="applicant_message" maxLength={2000} /></label>}
      <div className={styles.actions}>
        <button className={styles.button} disabled={busy || !ready}>{busy ? 'Approving' : 'Approve application'}</button>
        {onAnswer && <>
          <button type="button" className={styles.secondary} disabled={busy} onClick={(e) => void onAnswer('needs_info', e.currentTarget.form!)}>Ask for more</button>
          <button type="button" className={styles.secondary} disabled={busy} onClick={(e) => void onAnswer('declined', e.currentTarget.form!)}>Decline</button>
        </>}
      </div>
    </form>
  );
}
